import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Auth, getRedirectResult, UserCredential } from '@angular/fire/auth';
import { IonApp, IonRouterOutlet, IonSpinner } from '@ionic/angular/standalone';
import { PdfViewerWrapperModule } from './components/pdf-viewer/pdf-wrapper.module';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [CommonModule, IonApp, IonRouterOutlet, IonSpinner, PdfViewerWrapperModule],
})
export class AppComponent implements OnInit {
  isInitializing = true;
  private fontLoaded = false;
  private authChecked = false;

  constructor(private auth: Auth) {
    // Wait for Boogaloo font to load before showing app
    this.waitForFontLoad();
  }

  private async waitForFontLoad(): Promise<void> {
    // Check if Font Loading API is available
    if ('fonts' in document) {
      try {
        // Wait for all fonts to be ready
        await document.fonts.ready;

        // Poll until Boogaloo font is actually loaded (more reliable than single check)
        const checkFont = async (): Promise<void> => {
          const maxAttempts = 100; // 5 seconds total (50ms * 100)
          let attempts = 0;

          while (attempts < maxAttempts) {
            // Check if Boogaloo font is loaded
            if (document.fonts.check('1em "Boogaloo"')) {
              // Font is loaded - show app
              const appRoot = document.querySelector('app-root');
              if (appRoot) {
                appRoot.classList.add('font-loaded');
              }
              this.fontLoaded = true;
              this.checkInitializationComplete();
              return;
            }

            // Wait a bit before checking again
            await new Promise((resolve) => setTimeout(resolve, 50));
            attempts++;
          }

          // Timeout reached - show app anyway (font might still be loading)
          console.warn('Boogaloo font check timeout - showing app anyway');
          const appRoot = document.querySelector('app-root');
          if (appRoot) {
            appRoot.classList.add('font-loaded');
          }
          this.fontLoaded = true;
          this.checkInitializationComplete();
        };

        // Start checking
        await checkFont();
      } catch (error) {
        console.error('Error checking font load:', error);
        // Fallback: show app after longer delay if font check fails
        setTimeout(() => {
          const appRoot = document.querySelector('app-root');
          if (appRoot) {
            appRoot.classList.add('font-loaded');
          }
          this.fontLoaded = true;
          this.checkInitializationComplete();
        }, 2000);
      }
    } else {
      // Font Loading API not available - use longer fallback
      // Wait for font to load via CSS font-display: swap
      setTimeout(() => {
        const appRoot = document.querySelector('app-root');
        if (appRoot) {
          appRoot.classList.add('font-loaded');
        }
        this.fontLoaded = true;
        this.checkInitializationComplete();
      }, 2000);
    }
  }

  private checkInitializationComplete(): void {
    if (this.fontLoaded && this.authChecked) {
      // Small delay to ensure smooth transition
      setTimeout(() => {
        this.isInitializing = false;
      }, 300);
    }
  }

  async ngOnInit() {
    try {
      const result: UserCredential | null = await getRedirectResult(this.auth);
      if (result?.user) {
        console.log('Redirect result user:', result.user.email);
      } else {
        console.log('No redirect result. Maybe already signed in?');
      }
    } catch (err) {
      console.error('Redirect result error:', err);
    } finally {
      this.authChecked = true;
      this.checkInitializationComplete();
    }
  }
}
